/**
 * 
 * If you're calling the window.iw.stretchText function, make sure you have
 * set the CSS correctly: use the SCSS mixin stretch-text-parent() for the parent
 * container and stretch-text() for the child container. 
 * 
 * The above is unnecessary if you're just using the .stretch-text-parent and
 * .stretch-text CSS classes on the elements.
 * 
 * You can add classes to the .stretch-text-parent element to initialise only on
 * certain viewport widths: .stretch-text-md (for medium/large screens only) and
 * .stretch-text-lg (for large screens only). (NOTE: it's the parent, not the text
 * element itself, that takes these classes.)
 * 
 */

import { throttle } from "lodash";
import { onSmallScreen, onLargeScreen } from "./breakpoint-detect";

declare const jQuery: any;
const $ = jQuery;

export function initStretchText(): void
{
	$(".stretch-text-parent").each(function () {
		initStretchTextParent(this);
	});
}

// Returns refresh function
export function initStretchTextItem($text, $parent, respectBounds = true, maxScaleRatio?: number): () => void
{
	$text = $($text);
	$parent = $($parent);
	if ($text.length === 0)
	{
		console.warn("stretch-text error: no $text");
		return () => {};
	}
	if ($parent.length === 0)
	{
		console.warn("stretch-text error: no $parent");
		return () => {};
	}
	const stretch = () => {
		stretchContainer($text, $parent, respectBounds, maxScaleRatio);
	};
	stretch();
	$(window).on("resize load", throttle(stretch, 80));
	return stretch;
}

function initStretchTextParent($parent, $text?, respectBounds?: boolean, maxScaleRatio?: number): () => void
{
	$parent = $($parent);
	$text = $text || $parent.find(".stretch-text");
	if ($text.length === 0)
	{
		console.warn("stretch-text error: no .stretch-text inside .stretch-text-parent:", $parent[0]);
		return;
	}
	respectBounds = (respectBounds === undefined) ?
		!$text.hasClass("stretch-text-no-respect-bounds") :
		respectBounds;
	maxScaleRatio = (maxScaleRatio === undefined) ?
		parseFloat($text.attr("data-max-scale-ratio")) || undefined :
		maxScaleRatio;
	let initialized: boolean = false;
	let returnFunc: () => void = () => {};
	const init = () => {
		if (initialized)
		{
			return;
		}
		if (($parent.hasClass("stretch-text-md") && onSmallScreen()) ||
			($parent.hasClass("stretch-text-lg") && !onLargeScreen()))
		{
			return;
		}
		initialized = true;
		returnFunc = initStretchTextItem($text, $parent, respectBounds, maxScaleRatio);
	};
	init();
	if (!initialized)
	{
		$(window).on("resize", throttle(init, 200, { trailing: true }));
	}
	return (): void => {
		returnFunc();
	};
}

function stretchContainer($text, $parent, respectBounds: boolean, maxScaleRatio?: number): void
{
	$text.css("transform", "none");
	$parent.css("height", "");
	const textWidth: number = $text.outerWidth();
	if (!textWidth)
	{
		console.warn("stretch-text error: no textWidth");
		return;
	}
	const parentWidth: number = ($parent.width() || 0) - (parseInt($parent.css("padding-left") || 0, 10) + parseInt($parent.css("padding-right") || 0, 10));
	if (!parentWidth)
	{
		console.warn("stretch-text error: no parentWidth");
		return;
	}
	let scaleRatio: number = parentWidth / textWidth;
	if (maxScaleRatio && scaleRatio > maxScaleRatio)
	{
		scaleRatio = maxScaleRatio;
	}
	const setRatio = (scaleRatio: number): void => {
		let transformValue: string = `scale(${scaleRatio})`;
		$text.css({ transform: transformValue });
		const oldParentHeight: number = $parent.height();
		const newParentHeight: number = oldParentHeight * scaleRatio;
		$parent.css({ height: newParentHeight });
		if (respectBounds)
		{
			const translateX: number = getTranslateXValueForRespectBounds($text, $parent);
			if (translateX)
			{
				transformValue = `translateX(${translateX}px) ${transformValue}`;
				$text.css({ transform: transformValue });
			}
		}
	};

	setRatio(scaleRatio);

	$text.trigger("stretched", {
		$text,
		$parent,
		ratio: scaleRatio,
		setRatio: (correctedRatio: number) => {
			$text.css("transform", "none");
			$parent.css("height", "");
			setRatio(correctedRatio);
		}
	});
}

function getTranslateXValueForRespectBounds($text, $parent): number
{	
	const text: HTMLElement = $text[0];
	const textRect = text.getBoundingClientRect();
	const parent: HTMLElement = $parent[0];
	const parentRect = parent.getBoundingClientRect();
	let textLeft: number = textRect.left;

	// correct for right-hand side overspill
	const rightSpill: number = textRect.right - parentRect.right;
	let translateX = 0;
	if (rightSpill > 0)
	{
		textLeft -= rightSpill;
		translateX = -rightSpill;
	}

	// correct for left-hand side overspill
	const leftSpill: number = parentRect.left - textLeft;
	if (leftSpill > 0)
	{
		translateX += leftSpill;
	}
	return translateX;
}

if (!window["iw"])
{
	window["iw"] = {};
}

window["iw"].stretchText = ($text, $parent, respectBounds?: boolean, maxScaleRatio?: number): () => void => {
	return initStretchTextParent($parent, $text, respectBounds, maxScaleRatio);
};
